import { useState } from "react";
import { TgScreen } from "../components/TgScreen";
import { Card } from "../components/atoms";
import { useUnlockedWallet } from "../wallet";
import { isBackedUp, markBackedUp } from "../lib/backup-state";
import { haptic } from "../lib/haptics";

interface Props {
  onClose: () => void;
}

/**
 * Reveal-and-acknowledge the BIP-39 recovery phrase.
 *
 * Two stages: a gated "warning" view that hides the words behind a
 * tap (so they don't show up in screen-share or shoulder-surf by
 * accident) and the actual phrase view. The user must scroll all
 * twelve into focus + tap "I've saved this" to mark the wallet as
 * backed up — that's what dismisses the persistent banner on the
 * Wallet tab.
 *
 * Already-backed-up users get the same screen but with the banner
 * dismissal as a no-op confirm. Phrase is always re-revealable; that's
 * the whole point of dropping the "verify these words" challenge.
 */
export function BackupScreen({ onClose }: Props) {
  const wallet = useUnlockedWallet();
  const [revealed, setRevealed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(() => isBackedUp());

  if (!wallet) {
    return (
      <TgScreen title="Recovery phrase" onClose={onClose}>
        <div style={{ padding: "40px 24px", textAlign: "center", color: "var(--hint)" }}>
          Wallet not unlocked.
        </div>
      </TgScreen>
    );
  }

  const words = wallet.phrase.split(" ");

  const confirmAcknowledge = () => {
    markBackedUp();
    setAcknowledged(true);
    haptic("success");
    // Give the user the satisfaction beat, then close.
    window.setTimeout(onClose, 700);
  };

  return (
    <TgScreen title="Recovery phrase" onClose={onClose}>
      <div style={{ padding: "12px 16px 24px" }}>
        <Card
          style={{
            padding: 14,
            marginBottom: 12,
            background: "rgba(255, 200, 80, 0.07)",
            border: "1px solid rgba(255, 200, 80, 0.2)",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Write this down somewhere safe.
          </div>
          <div style={{ fontSize: 12, color: "var(--hint)", lineHeight: 1.5 }}>
            These 12 words restore your wallet if you lose access to your
            Telegram account. Anyone who has them can spend your funds —
            don't paste them online, don't send them in chat, don't store
            them in a password manager you share.
          </div>
        </Card>

        {!revealed ? (
          <button
            onClick={() => {
              haptic("warning");
              setRevealed(true);
            }}
            style={{
              width: "100%",
              background: "var(--bg-2, #1f2429)",
              border: "1px dashed var(--hairline, #2c333a)",
              borderRadius: 12,
              padding: "40px 16px",
              fontSize: 14,
              fontWeight: 600,
              color: "var(--text)",
              cursor: "pointer",
              fontFamily: "var(--font-text)",
            }}
            data-testid="backup-reveal-button"
          >
            👁️ Tap to reveal your 12 words
          </button>
        ) : (
          <Card style={{ padding: 14 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px 12px",
              }}
            >
              {words.map((w, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 8,
                    background: "var(--bg-3, #181d22)",
                    borderRadius: 8,
                    padding: "8px 10px",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--hint)",
                      fontWeight: 600,
                      minWidth: 16,
                      textAlign: "right",
                    }}
                  >
                    {i + 1}.
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "var(--text)",
                      fontFamily: "var(--font-mono, ui-monospace, monospace)",
                      letterSpacing: "-0.005em",
                    }}
                  >
                    {w}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {revealed && (
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(wallet.phrase);
                  haptic("success");
                } catch {
                  // older clients — fall back: user manually selects
                }
              }}
              style={{
                flex: 1,
                background: "var(--bg-2, #1f2429)",
                color: "var(--text)",
                border: "1px solid var(--hairline, #2c333a)",
                borderRadius: 10,
                padding: "12px 14px",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "var(--font-text)",
              }}
            >
              Copy
            </button>
            <button
              onClick={confirmAcknowledge}
              disabled={acknowledged}
              style={{
                flex: 1,
                background: acknowledged
                  ? "rgba(46, 187, 96, 0.18)"
                  : "var(--tg-theme-button-color, #2c8acb)",
                color: acknowledged
                  ? "var(--text)"
                  : "var(--tg-theme-button-text-color, white)",
                border: "none",
                borderRadius: 10,
                padding: "12px 14px",
                fontSize: 14,
                fontWeight: 600,
                cursor: acknowledged ? "default" : "pointer",
                fontFamily: "var(--font-text)",
                transition: "background 0.15s",
              }}
            >
              {acknowledged ? "✓ Saved" : "I've saved this"}
            </button>
          </div>
        )}

        {acknowledged && (
          <div
            style={{
              marginTop: 14,
              fontSize: 12,
              color: "var(--hint)",
              textAlign: "center",
            }}
          >
            You can come back to view this phrase any time.
          </div>
        )}
      </div>
    </TgScreen>
  );
}
